import {
  Avatar,
  CollapseCard,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { contactsApi } from 'apis/contacts/contacts.api';
import { ContactMemoryDimension } from 'apis/contacts/types/contactsApi.types';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';

import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from '../../components/visibilityToggle/visibilityToggle';

import styles from './contactMemoriesCard.module.scss';

type ContactMemoriesCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  isMinimized: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

const dimensionLabels: Record<ContactMemoryDimension, string> = {
  commitment: 'Commitment',
  exception: 'Exception',
  preference: 'Preference',
  relationship_signal: 'Relationship signal',
};

export const ContactMemoriesCard = ({
  isMinimized,
  isOpen,
  index,
  type,
  onMove,
  onToggle,
}: ContactMemoriesCardProps): JSX.Element | null => {
  const row = useRef<HTMLDivElement>(null);
  const contactId = useAppSelector(
    (state) => state.conversation.metaConversation.requester?.id,
  );

  const { data, isLoading, isError } = contactsApi.useReadContactMemoriesQuery(
    { contactId: contactId ?? '', limit: 10 },
    { skip: !contactId },
  );

  const [dismissMemory] = contactsApi.useDismissContactMemoryMutation();

  if (!contactId) {
    return null;
  }

  const avatar = <Avatar icon="lightbulb" />;

  if (isMinimized) {
    return (
      <FlexBox flexDirection="column" gap="100">
        <div className="icon-holder">{avatar}</div>
      </FlexBox>
    );
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <FlexBox justifyContent="center">
          <Spinner />
        </FlexBox>
      );
    }

    if (isError) {
      return (
        <Text.Paragraph as="p" variant="3" subdued>
          Couldn&apos;t load highlights.
        </Text.Paragraph>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Text.Paragraph as="p" variant="3" subdued>
          No highlights to show.
        </Text.Paragraph>
      );
    }

    return data.map((memory) => {
      const dimensionClass = classnames(styles.defaultDimension, {
        [styles.dimensionCommitment]: memory.dimension === 'commitment',
        [styles.dimensionException]: memory.dimension === 'exception',
        [styles.dimensionPreference]: memory.dimension === 'preference',
        [styles.dimensionRelationshipSignal]:
          memory.dimension === 'relationship_signal',
      });
      const label = dimensionLabels[memory.dimension] ?? memory.dimension;

      return (
        <FlexBox key={memory.id} alignItems="stretch" gap="200">
          <div className={classnames(styles.indicatorBar, dimensionClass)} />
          <DropdownButton
            noStyle
            noArrow
            className={styles.contentTrigger}
            buttonProps={{ tabIndex: 0 }}
            dropdown={
              <DropdownButtonContext.Consumer>
                {({ closeDropdown }) => (
                  <FlexBox
                    flexDirection="column"
                    gap="200"
                    padding="200"
                    maxWidth="320px"
                  >
                    <FlexBox
                      alignItems="center"
                      gap="100"
                      className={styles.dimensionLabel}
                    >
                      <div
                        className={classnames(
                          styles.dimensionDot,
                          dimensionClass,
                        )}
                      />
                      <Text.Paragraph as="p" variant="4">
                        {label}
                      </Text.Paragraph>
                    </FlexBox>
                    {memory.quote && (
                      <Text.Paragraph
                        as="p"
                        variant="3"
                        className={styles.quote}
                      >
                        &ldquo;{memory.quote}&rdquo;
                      </Text.Paragraph>
                    )}
                    <Link
                      to={`/conversation/${memory.csid}`}
                      onClick={closeDropdown}
                    >
                      View conversation #{memory.csid}
                    </Link>
                  </FlexBox>
                )}
              </DropdownButtonContext.Consumer>
            }
          >
            <Text.Paragraph as="p" variant="3" className={styles.content}>
              {memory.content}
            </Text.Paragraph>
          </DropdownButton>
          <Tooltip content="Dismiss as not relevant">
            <IconButton
              icon="thumbs-down"
              variant="tertiary"
              aria-label="Dismiss memory"
              onClick={() => dismissMemory({ contactId, memoryId: memory.id })}
            />
          </Tooltip>
        </FlexBox>
      );
    });
  };

  return (
    <CollapseCard containerRef={row} isOpen={isOpen} onToggle={onToggle}>
      <CollapseCard.Header
        avatar={avatar}
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
        slotAfterTitle={<VisibilityToggle />}
      >
        Customer highlights
      </CollapseCard.Header>
      <CollapseCard.Content>
        <FlexBox flexDirection="column" gap="200" padding="0 0 300 0">
          {renderContent()}
        </FlexBox>
      </CollapseCard.Content>
    </CollapseCard>
  );
};
